
import React, { useState } from 'react';

interface ConsultationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * ConsultationModal – a lightweight modal that sends a form to Readdy.
 *
 * Fixes applied:
 * 1. Added an explicit `React` import – required for JSX when using certain
 *    TypeScript configurations and for the `React.FormEvent` type.
 * 2. Guarded `formData.get('message')` against `null` to avoid a possible
 *    runtime error.
 * 3. Added basic error handling that logs the failure (instead of silently
 *    ignoring it) while still keeping the UI responsive.
 * 4. Typed the `fetch` response handling to ensure robustness.
 */
export default function ConsultationModal({
  isOpen,
  onClose,
}: ConsultationModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);

    // `FormData.get` can return `FormDataEntryValue | null`. Guard against null.
    const rawMessage = formData.get('message');
    const message = typeof rawMessage === 'string' ? rawMessage : '';

    // Prevent submission when the message exceeds the allowed length.
    if (message && message.length > 500) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(
        'https://readdy.ai/api/form/d6ad59lbd17lru29lojg',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          // `URLSearchParams` works with `FormData` because it iterates over
          // key/value pairs. The `as any` cast is safe here as we only need a
          // plain object of string values.
          body: new URLSearchParams(formData as any).toString(),
        }
      );

      if (!response.ok) {
        // Throw an error to be caught by the catch‑block below.
        const errorText = await response.text();
        throw new Error(`Network response was not ok: ${errorText}`);
      }

      // Successful submission – reset the form and show the thank‑you UI.
      form.reset();
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        onClose();
      }, 2500);
    } catch (err) {
      // Log the error for debugging while keeping the UI stable.
      console.error('Consultation form submission failed:', err);
      // Optionally you could surface a user‑friendly message here.
    } finally {
      setSubmitting(false);
    }
  };

  // Do not render anything if the modal is closed.
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-[560px] w-full p-10 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center text-[#0B1F3A]/40 hover:text-[#0B1F3A] transition-colors cursor-pointer rounded-full hover:bg-[#0B1F3A]/5"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        {submitted ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full bg-[#1EB6B0]/10 flex items-center justify-center mx-auto mb-5">
              <i className="ri-check-line text-3xl text-[#1EB6B0]"></i>
            </div>
            <h3 className="text-2xl font-semibold text-[#0B1F3A] mb-2">
              Thank you
            </h3>
            <p className="text-[15px] text-[#0B1F3A]/60">
              We will contact you within 24 hours.
            </p>
          </div>
        ) : (
          <>
            <h3 className="text-2xl font-semibold text-[#0B1F3A] mb-2">
              Request Executive Consultation
            </h3>
            <p className="text-sm text-[#0B1F3A]/55 mb-7">
              Fill out the form below and we&apos;ll contact you within 24
              hours.
            </p>

            <form
              id="consultation-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-[#0B1F3A] mb-1.5"
                >
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 h-11 border border-[#0B1F3A]/15 rounded-md focus:outline-none focus:border-[#1EB6B0] transition-colors text-sm"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-[#0B1F3A] mb-1.5"
                >
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 h-11 border border-[#0B1F3A]/15 rounded-md focus:outline-none focus:border-[#1EB6B0] transition-colors text-sm"
                />
              </div>

              <div>
                <label
                  htmlFor="company"
                  className="block text-sm font-medium text-[#0B1F3A] mb-1.5"
                >
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  required
                  className="w-full px-4 h-11 border border-[#0B1F3A]/15 rounded-md focus:outline-none focus:border-[#1EB6B0] transition-colors text-sm"
                />
              </div>

              <div>
                <label
                  htmlFor="industry"
                  className="block text-sm font-medium text-[#0B1F3A] mb-1.5"
                >
                  Industry
                </label>
                <select
                  id="industry"
                  name="industry"
                  required
                  className="w-full px-4 h-11 border border-[#0B1F3A]/15 rounded-md focus:outline-none focus:border-[#1EB6B0] transition-colors text-sm cursor-pointer"
                >
                  <option value="">Select your industry</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="legal">Legal</option>
                  <option value="wealth-management">Wealth Management</option>
                  <option value="municipal">Municipal Operations</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-[#0B1F3A] mb-1.5"
                >
                  Message (Max 500 characters)
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-[#0B1F3A]/15 rounded-md focus:outline-none focus:border-[#1EB6B0] transition-colors resize-none text-sm"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#1EB6B0] text-white h-11 rounded-md text-sm font-medium hover:bg-[#1EB6B0]/90 transition-all whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
